import { useState, useCallback, useMemo } from "react";
import type { Product } from "@/lib/validation/product";

interface UseProductSelectionReturn {
  readonly selectedIds: ReadonlySet<string>;
  readonly isAllPageSelected: boolean;
  readonly isAllFilteredSelected: boolean;
  readonly isSelected: (id: string) => boolean;
  readonly toggle: (id: string) => void;
  readonly toggleAll: (pageProducts: readonly Product[]) => void;
  readonly selectAllFiltered: (filteredIds: readonly string[]) => void;
  readonly clear: () => void;
  readonly count: number;
}

/**
 * Manages product selection state with page-scoped and filter-scoped modes.
 * Gmail-style: "All 25 on this page selected. Select all 47 filtered?"
 */
export function useProductSelection(
  pageProducts: readonly Product[],
): UseProductSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const pageIds = useMemo(
    () => new Set(pageProducts.map((p) => p.id)),
    [pageProducts],
  );

  const isAllPageSelected =
    pageIds.size > 0 && [...pageIds].every((id) => selectedIds.has(id));

  const [isAllFilteredSelected, setIsAllFilteredSelected] = useState(false);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setIsAllFilteredSelected(false);
  }, []);

  const toggleAll = useCallback((products: readonly Product[]) => {
    setSelectedIds((prev) => {
      const allSelected = products.every((p) => prev.has(p.id));
      if (allSelected) {
        // Deselect all on page
        const next = new Set(prev);
        for (const p of products) {
          next.delete(p.id);
        }
        return next;
      }
      // Select all on page
      const next = new Set(prev);
      for (const p of products) {
        next.add(p.id);
      }
      return next;
    });
    setIsAllFilteredSelected(false);
  }, []);

  const selectAllFiltered = useCallback((filteredIds: readonly string[]) => {
    setSelectedIds(new Set(filteredIds));
    setIsAllFilteredSelected(true);
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setIsAllFilteredSelected(false);
  }, []);

  return {
    selectedIds,
    isAllPageSelected,
    isAllFilteredSelected,
    isSelected,
    toggle,
    toggleAll,
    selectAllFiltered,
    clear,
    count: selectedIds.size,
  } as const;
}
